import { useState } from 'react';

import { generateLotteryShareImage, getLotteryShareTweetText } from '@/api/pointsLottery';

interface ShareImageState {
  imageUrl: string | null;
  tweetText: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for generating and managing lottery share images
 */
export function useLotteryShareImage() {
  const [state, setState] = useState<ShareImageState>({
    imageUrl: null,
    tweetText: null,
    isLoading: false,
    error: null,
  });

  /**
   * Generate share image for lottery win
   */
  const generateImage = async (winnerId: number, seasonId: number) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await generateLotteryShareImage({ winnerId, seasonId });

      setState({
        imageUrl: result.imageUrl,
        tweetText: result.tweetText,
        isLoading: false,
        error: null,
      });

      return { success: true, ...result };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate share image';
      setState({
        imageUrl: null,
        tweetText: null,
        isLoading: false,
        error: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  /**
   * Get tweet text without generating full image (faster)
   */
  const getTweetText = async (winnerId: number, seasonId: number) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await getLotteryShareTweetText({ winnerId, seasonId });

      setState(prev => ({
        ...prev,
        tweetText: result.tweetText,
        isLoading: false,
        error: null,
      }));

      return { success: true, tweetText: result.tweetText, twitterShareUrl: result.twitterShareUrl };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get tweet text';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      return { success: false, error: errorMessage };
    }
  };

  /**
   * Clean up blob URL when done
   */
  const cleanup = () => {
    if (state.imageUrl) {
      URL.revokeObjectURL(state.imageUrl);
    }
    setState({
      imageUrl: null,
      tweetText: null,
      isLoading: false,
      error: null,
    });
  };

  return {
    ...state,
    generateImage,
    getTweetText,
    cleanup,
  };
}
